import { z } from "zod";
import { DateTimeWithOffset, NullishDateTimeWithOffset } from "@/lib/api/schemas/patterns";
import { ProjectType } from "@/lib/domain/platform";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { KY_OPTIONS } from "@/config";
import { apiClient as ky } from "../kyConfig";
import { logApi } from "../logger";
import { request, validateInput } from "../request";

class ArtifactsEndpoint {
  public static readonly basePath = "artifacts";
  public static readonly schemas = {
    list: {
      response: z.object({
        count: z.number(),
        items: z
          .object({
            id: z.number(),
            created_at: DateTimeWithOffset,
            name: z.string().default(""),
            url: z.string().nullable(),
            file_url: z.string().nullable(),
            metadata: z.record(z.any()).nullable(),
            project_type: ProjectType.nullable(),
            last_used_at: NullishDateTimeWithOffset,
          })
          .array(),
      }),
    },
    upload: {
      response: z.object({
        id: z.number(),
        created_at: DateTimeWithOffset,
        name: z.string(),
        url: z.string().nullable(),
        file_url: z.string(),
      }),
    },
    read: {
      response: z.object({
        id: z.number(),
        created_at: DateTimeWithOffset,
        name: z.string().default(""),
        url: z.string().nullable(),
        file_url: z.string().nullable(),
        metadata: z.record(z.any()).nullable(),
        project_type: ProjectType.nullable(),
        is_deleted: z.boolean().default(false),
      }),
    },
    update: {
      body: z.object({
        name: z.string().nullable(),
        url: z.string().nullable(),
      }),
    },
  };

  constructor(private api: Auth) {}

  public list(query: {
    project_id: string;
  }): ActionReturnType<z.infer<typeof ArtifactsEndpoint.schemas.list.response>> {
    return request({
      log: ["Artifacts", "list"],
      method: "get",
      path: [ArtifactsEndpoint.basePath],
      query,
      response: ArtifactsEndpoint.schemas.list.response,
    });
  }

  public async upload(
    file: File,
    query: { project_id: string },
  ): ActionReturnType<z.infer<typeof ArtifactsEndpoint.schemas.upload.response>> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    const url = this.api.apiUrl([ArtifactsEndpoint.basePath, query.project_id, "add-artifact"]);

    let data: any;
    try {
      data = await ky
        .post(url, {
          body: formData,
          headers: this.api.getAuthHeader(),
          ...KY_OPTIONS,
          timeout: 180000,
          retry: 0,
        })
        .json();
    } catch (error: any) {
      let errorResponse: any = null;
      if (error?.response) {
        try {
          errorResponse = await error.response.clone().json();
        } catch {
          try {
            errorResponse = await error.response.clone().text();
          } catch {}
        }
      }

      if (errorResponse?.detail) {
        const errorMsg = Array.isArray(errorResponse.detail)
          ? errorResponse.detail.map((d: any) => d.msg || JSON.stringify(d)).join("; ")
          : errorResponse.detail;
        return {
          ok: false,
          type: "request",
          error: errorMsg,
        };
      }

      return {
        ok: false,
        type: "request",
        error: error instanceof Error ? error.message : "An unknown error occurred",
      };
    }

    logApi("Artifacts", "upload", "response", data);
    const parsedData = ArtifactsEndpoint.schemas.upload.response.safeParse(data);
    logApi("Artifacts", "upload", "parsed", parsedData);

    if (!parsedData.success) {
      return {
        ok: false,
        type: "response_validation",
        error: parsedData.error.message,
      };
    }

    return {
      ok: true,
      data: parsedData.data,
    };
  }

  public async create(
    body: { name: string; url?: string | null },
    query: { project_id: string },
  ): ActionReturnType<z.infer<typeof ArtifactsEndpoint.schemas.read.response>> {
    const formData = new FormData();
    formData.append("name", body.name);
    if (body.url) {
      formData.append("url", body.url);
    }

    const url = this.api.apiUrl([ArtifactsEndpoint.basePath, query.project_id, "add-artifact"]);

    let data: any;
    try {
      data = await ky
        .post(url, {
          body: formData,
          headers: this.api.getAuthHeader(),
          ...KY_OPTIONS,
        })
        .json();
    } catch (error: any) {
      let errorResponse: any = null;
      if (error?.response) {
        try {
          errorResponse = await error.response.clone().json();
        } catch {
          try {
            errorResponse = await error.response.clone().text();
          } catch {}
        }
      }

      if (errorResponse?.detail) {
        const errorMsg = Array.isArray(errorResponse.detail)
          ? errorResponse.detail.map((d: any) => d.msg || JSON.stringify(d)).join("; ")
          : errorResponse.detail;
        return {
          ok: false,
          type: "request",
          error: errorMsg,
        };
      }

      return {
        ok: false,
        type: "request",
        error: error instanceof Error ? error.message : "An unknown error occurred",
      };
    }

    logApi("Artifacts", "create", "response", data);
    const parsedData = ArtifactsEndpoint.schemas.read.response.passthrough().safeParse(data);
    logApi("Artifacts", "create", "parsed", parsedData);

    if (!parsedData.success) {
      return {
        ok: false,
        type: "response_validation",
        error: parsedData.error.message,
      };
    }

    return {
      ok: true,
      data: parsedData.data,
    };
  }

  public read(id: string | number): ActionReturnType<z.infer<typeof ArtifactsEndpoint.schemas.read.response>> {
    return request({
      log: ["Artifacts", "read"],
      method: "get",
      path: [ArtifactsEndpoint.basePath, id],
      response: ArtifactsEndpoint.schemas.read.response,
    });
  }

  public async update(
    id: string,
    body: z.input<typeof ArtifactsEndpoint.schemas.update.body>,
  ): ActionReturnType<undefined> {
    const bodyValidated = validateInput(ArtifactsEndpoint.schemas.update.body, body);
    if (!bodyValidated.ok) return bodyValidated;
    return request({
      log: ["Artifacts", "update"],
      method: "post",
      path: [ArtifactsEndpoint.basePath, id, "update"],
      json: bodyValidated.data,
    });
  }

  public delete(id: string | number): ActionReturnType<undefined> {
    return request({
      log: ["Artifacts", "delete"],
      method: "delete",
      path: [ArtifactsEndpoint.basePath, id],
    });
  }
}

export { ArtifactsEndpoint };
